import { ThemeEvents } from '@theme/events';
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  #nlaVariantGeneration = 0;
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#nlaVariantGeneration++;
    const feedback = this.querySelector('[data-nla-variant-feedback]');
    if (feedback && feedback.textContent && !feedback.classList.contains('visually-hidden')) feedback.textContent = this.dataset.nlaVariantError;
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * Handles product select event and updates the price.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    if (event.target.dataset.productId && this.dataset.productId && event.target.dataset.productId !== this.dataset.productId) return;

    const generation = ++this.#nlaVariantGeneration;
    const feedback = this.querySelector('[data-nla-variant-feedback]');
    const oldPrice = this.refs.priceContainer;
    const oldVolumeNote = this.refs.volumePricingNote;
    const wasHidden = oldPrice?.hidden;
    const volumeWasHidden = oldVolumeNote?.hidden;
    if (feedback && oldPrice) {
      oldPrice.hidden = true;
      if (oldVolumeNote) oldVolumeNote.hidden = true;
      feedback.classList.remove('visually-hidden');
      feedback.textContent = this.dataset.nlaVariantLoading;
    }

    event.promise
      .then(({ detail }) => {
        if (generation !== this.#nlaVariantGeneration) return;
        if (!detail?.html) throw new Error('Variant response missing HTML');

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          if (feedback && oldPrice) { oldPrice.hidden = wasHidden; if (oldVolumeNote) oldVolumeNote.hidden = volumeWasHidden; feedback.textContent = ''; }
          return;
        }

        const { priceContainer, volumePricingNote } = this.refs;
        // Find the new product-price element in the updated HTML
        const newProductPrice = html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`);
        if (!newProductPrice) throw new Error('Updated price component missing');

        // Update price container
        const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
        if (!newPrice || !priceContainer) throw new Error('Updated price missing');
        priceContainer.replaceWith(newPrice);

        // Update volume pricing note
        const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');
        if (!newNote) {
          volumePricingNote?.remove();
        } else if (!volumePricingNote) {
          // Use newPrice since priceContainer was just replaced and now points to the detached element
          newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
        } else {
          volumePricingNote.replaceWith(newNote);
        }

        // Update installments (SPI banner) variant ID to trigger payment terms re-render
        const installmentsInput = /** @type {HTMLInputElement|null} */ (
          this.querySelector(`#product-form-installment-${this.dataset.blockId} input[name="id"]`)
        );
        if (installmentsInput) {
          installmentsInput.value = detail.resource?.id ?? '';
          installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        if (feedback) {
          feedback.classList.add('visually-hidden');
          const currentPrice = newPrice.querySelector('.price');
          const priceText = currentPrice?.textContent.trim().replace(/\s+/g, ' ') ?? '';
          feedback.textContent = [this.dataset.nlaVariantUpdated, priceText].filter(Boolean).join(' ');
        }
      })
      .catch((error) => {
        if (generation !== this.#nlaVariantGeneration) return;
        if (feedback && oldPrice) { feedback.classList.remove('visually-hidden'); feedback.textContent = this.dataset.nlaVariantError; }
        if (error?.name !== 'AbortError') console.warn('[product-price] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
